import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@sequelae/lore-core';
import { CommunicationBridge } from '@sequelae/lore-mfe-kit';
import './NavigationTestButtons.css';

interface PayloadData {
  type: string;
  payload: {
    url: string;
    scope: string;
    module: string;
    path: string;
  };
  timestamp: string;
}

interface NavigationTarget {
  number: number;
  label: string;
  path: string;
  module: string;
  scope: string;
  url?: string; // Optional: for cross-MFE navigation
}

// Menu MFE internal navigation targets
const MENU_NAVIGATION_TARGETS: NavigationTarget[] = [
  {
    number: 1,
    label: 'Android Menu',
    path: '/menu',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 2,
    label: 'iOS Menu',
    path: '/ios',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 3,
    label: 'Android Account',
    path: '/account',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 4,
    label: 'iOS Account',
    path: '/account-ios',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 5,
    label: 'Android About',
    path: '/about',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 6,
    label: 'iOS About',
    path: '/about-ios',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 7,
    label: 'Android Privacy',
    path: '/about/privacy',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 8,
    label: 'iOS Privacy',
    path: '/about-ios/privacy',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 9,
    label: 'Android Terms',
    path: '/about/terms',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
  {
    number: 10,
    label: 'iOS Terms',
    path: '/about-ios/terms',
    module: 'MenuMFE',
    scope: 'MenuMFE',
  },
];

// Cross-MFE navigation targets (to other MFEs)
const CROSS_MFE_TARGETS: NavigationTarget[] = [
  {
    number: 1,
    label: '🩺 Diagnostics',
    path: '/',
    module: 'DiagnosticsApp',
    scope: 'diagnostics',
    url: 'https://lore-diagnostics-mfe-nonprod.storage.googleapis.com',
  },
  {
    number: 2,
    label: '🤖 LoreBot',
    path: '/',
    module: 'LoreBotChat',
    scope: 'RemoteLoreBot',
    url: 'https://lb-ui-nonprod-lore-00.endpoints.protected-00-349215.cloud.goog',
  },
  {
    number: 3,
    label: '🔬 Explorations',
    path: '/',
    module: 'Explorations',
    scope: 'Explorations',
    url: 'https://explorations-non-prod.lorehealthcare.com',
  },
  {
    number: 4,
    label: '👥 Community',
    path: '/',
    module: 'LoreCommunityModule',
    scope: 'LoreCommunityScope',
    url: 'https://lore-community-mfe-nonprod.storage.googleapis.com',
  },
  {
    number: 5,
    label: '🎁 Rewards',
    path: '/',
    module: 'Rewards',
    scope: 'Rewards',
    url: 'https://rewards-non-prod.lorehealthcare.com',
  },
  {
    number: 6,
    label: '✅ Verification',
    path: '/',
    module: 'Verification',
    scope: 'RemoteOnboarding',
    url: 'https://lore-verification-service-app-eex6i6xwzq-uc.a.run.app',
  },
  {
    number: 7,
    label: '🔥 Streaks',
    path: '/',
    module: 'StreaksPage',
    scope: 'RemoteStreaks',
    url: 'https://lore-streaks-ui-nonprod.web.app',
  },
  {
    number: 8,
    label: '⚙️ User Preferences',
    path: '/',
    module: 'UserPreferences',
    scope: 'UserPreferences',
    url: 'https://user-preferences-nonprod.lorehealthcare.io',
  },
];

export const NavigationTestButtons: React.FC = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [lastPayload, setLastPayload] = useState<PayloadData | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasNewPayload, setHasNewPayload] = useState(false);

  // Filter out the current page from the menu navigation targets
  const availableMenuTargets = MENU_NAVIGATION_TARGETS.filter(
    target => target.path !== currentPath
  );

  // Listen for payload events
  useEffect(() => {
    const handlePayloadSent = (event: CustomEvent<PayloadData>) => {
      console.log('✅ Payload display received event:', event.detail);
      setLastPayload(event.detail);
      setHasNewPayload(true);
      // Don't auto-expand, let user click to expand
      // setIsExpanded(true);
    };

    window.addEventListener('nativePushSent', handlePayloadSent as EventListener);

    return () => {
      window.removeEventListener('nativePushSent', handlePayloadSent as EventListener);
    };
  }, []);

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    setHasNewPayload(false); // Clear the "new" indicator when user expands
  };

  const handleNativePush = (target: NavigationTarget) => {
    // Use target's URL if provided (cross-MFE), otherwise use Menu MFE URL
    const baseUrl = target.url || 'https://menu-mfe-nonprod.lorehealthcare.io';

    const payload = {
      type: 'MFE_MODULE_OPEN',
      payload: {
        url: baseUrl, // Base URL only, no hash
        scope: target.scope,
        module: target.module,
        path: target.path, // Path sent separately
      },
      timestamp: new Date().toLocaleTimeString(),
    };

    console.log('🧭 CommunicationBridge.openMFEModule: Navigating to', target.label);
    console.log('📦 URL:', baseUrl);
    console.log('📦 Path:', target.path);
    console.log('📦 Full Payload:', payload);

    // Use CommunicationBridge to open MFE module
    CommunicationBridge.openMFEModule({
      url: baseUrl,
      scope: target.scope,
      module: target.module,
      path: target.path,
    });

    // Dispatch custom event so NavigationPayloadDisplay can show it
    window.dispatchEvent(
      new CustomEvent('nativePushSent', {
        detail: payload,
      })
    );
  };

  return (
    <div className="navigation-test-container">
      {/* Menu MFE Internal Navigation */}
      {availableMenuTargets.length > 0 && (
        <div className="navigation-test-buttons">
          <div className="navigation-test-header">
            <span className="navigation-test-title">🧪 Menu MFE Navigation</span>
          </div>
          <div className="navigation-test-grid">
            {availableMenuTargets.map(target => (
              <button
                key={target.path}
                className="navigation-test-button"
                onClick={() => handleNativePush(target)}
              >
                <span className="button-number">{target.number}</span> {target.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Cross-MFE Navigation */}
      <div className="navigation-test-buttons">
        <div className="navigation-test-header">
          <span className="navigation-test-title">🚀 Cross-MFE Navigation (Native Push)</span>
        </div>
        <div className="navigation-test-grid">
          {CROSS_MFE_TARGETS.map(target => (
            <button
              key={`${target.scope}-${target.path}`}
              className="navigation-test-button"
              onClick={() => handleNativePush(target)}
            >
              {target.label}
            </button>
          ))}
        </div>
      </div>

      {/* Additional Navigation Methods */}
      <div className="navigation-test-buttons">
        <div className="navigation-test-header">
          <span className="navigation-test-title">🔧 More Navigation Methods</span>
        </div>
        <div className="navigation-test-grid">
          <button
            className="navigation-test-button"
            onClick={() => {
              console.log('📲 Opening Diagnostics as Sheet');
              CommunicationBridge.navigateTo('/diagnostics', {
                type: 'sheet',
              });
            }}
          >
            Diagnostics (Sheet)
          </button>
          <button
            className="navigation-test-button"
            onClick={() => {
              console.log('🌐 Opening in Browser');
              CommunicationBridge.navigateTo('https://lorehealthcare.io', {
                type: 'browser',
              });
            }}
          >
            Lore Website
          </button>
          <button
            className="navigation-test-button"
            onClick={async () => {
              console.log('🔔 Checking notification status');
              const status = await CommunicationBridge.getNotificationStatus();
              console.log('Status:', status);
              alert(`Notification Status:\n${JSON.stringify(status, null, 2)}`);
            }}
          >
            Check Notifications
          </button>
          <button
            className="navigation-test-button"
            onClick={() => {
              console.log('✅ Calling onFinished');
              CommunicationBridge.onFinished();
            }}
          >
            Close MFE (onFinished)
          </button>
        </div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '8px', padding: '0 8px' }}>
          Sheet = modal overlay • Browser = external browser • onFinished = close MFE
        </div>
      </div>

      {/* Payload Display */}
      <div className="navigation-payload-inline">
        {!lastPayload ? (
          <div className="payload-toggle-inline">
            🎯 Click any navigation button above to test MFE navigation
          </div>
        ) : (
          <>
            <button className="payload-toggle-inline clickable" onClick={handleToggleExpand}>
              📤 Last MFE Navigation{' '}
              {hasNewPayload && !isExpanded && <span className="new-badge">NEW</span>}{' '}
              {isExpanded ? '▼' : '▶'}
            </button>

            {isExpanded && (
              <div className="payload-content-inline">
                <div className="payload-timestamp">⏰ {lastPayload.timestamp}</div>
                <div className="payload-section">
                  <div className="payload-label">Type:</div>
                  <div className="payload-value">{lastPayload.type}</div>
                </div>
                <div className="payload-section">
                  <div className="payload-label">URL (remoteURL):</div>
                  <div className="payload-value payload-url">{lastPayload.payload.url}</div>
                </div>
                <div className="payload-section">
                  <div className="payload-label">Scope:</div>
                  <div className="payload-value">{lastPayload.payload.scope}</div>
                </div>
                <div className="payload-section">
                  <div className="payload-label">Module:</div>
                  <div className="payload-value">{lastPayload.payload.module}</div>
                </div>
                <div className="payload-section">
                  <div className="payload-label">Path:</div>
                  <div className="payload-value">{lastPayload.payload.path}</div>
                </div>
                <div className="payload-json">
                  <div className="payload-label">Full Payload JSON:</div>
                  <pre>{JSON.stringify(lastPayload, null, 2)}</pre>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
